import React, { FunctionComponent } from 'react';
import { ActivationStatus, BudgetLimitsTransactionResponse } from '../../../../types.__generated__';
import { Button } from '../../../shared/Button';
import { Form, FormControl, FormFieldType } from '../../../shared/Form';
import { Paragraph } from '../../../shared/Paragraph';
import { PopUpHandler } from '../../../shared/PopupHandler';
import { useGamblingControlsValidationRules } from '../../config';
import { MediatorType, SetPlayerLimitsData } from '../../types';
import { IGroupedLimitsTypes, LimitPeriodsEnum } from '../types';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { getLimitProperties } from '../../helper';
import './AccountLimitsModal.scss';

type AccountLimitsModalProps = {
  /**
   * Function to handle close events
   */
  onClose: () => void;
  /**
   * function which is triggered on form submit
   */
  onSubmit: (data: SetPlayerLimitsData) => void;
  /**
   * Player limit data returned from spine
   */
  data?: IGroupedLimitsTypes;
  /**
   * boolean value to display Modal
   */
  isOpen: boolean;
  /**
   * Different mediator type
   */
  mediatorType: MediatorType;
};

/**
 * Component to contain edit and set modal
 */
export const AccountLimitsModal: FunctionComponent<AccountLimitsModalProps> = ({
  onClose,
  onSubmit,
  data,
  isOpen,
  mediatorType,
}) => {
  const { t } = useTranslations();
  const title = getLimitProperties(mediatorType, t).title;

  const getPendingItemByPeriod = (period: LimitPeriodsEnum): BudgetLimitsTransactionResponse | undefined => {
    return data?.[period]?.find((item) => item.activationStatus === ActivationStatus.PendingUserAction);
  };

  const getLimitAmount = (period: LimitPeriodsEnum): number | null => {
    const amount = data?.[period]?.find(
      (item) => item.activationStatus === ActivationStatus.Active && !item.isPendingCancellation
    )?.amount;
    const pendingAmount = getPendingItemByPeriod(period)?.amount;

    return amount || pendingAmount || null;
  };

  const defaultValues = {
    dailyLimit: getLimitAmount(LimitPeriodsEnum.daily),
    weeklyLimit: getLimitAmount(LimitPeriodsEnum.weekly),
    monthlyLimit: getLimitAmount(LimitPeriodsEnum.monthly),
  };

  const validation = useGamblingControlsValidationRules();

  const handleSubmit = (limits: {
    dailyLimit: string | number | null;
    weeklyLimit: string | number | null;
    monthlyLimit: string | number | null;
  }): void => {
    const daily = limits.dailyLimit ? parseInt(String(limits.dailyLimit)) : 0;
    const weekly = limits.weeklyLimit ? parseInt(String(limits.weeklyLimit)) : 0;
    const monthly = limits.monthlyLimit ? parseInt(String(limits.monthlyLimit)) : 0;

    const { type } = getLimitProperties(mediatorType, t);

    if (daily && defaultValues.dailyLimit !== daily) {
      onSubmit({
        amount: daily,
        period: LimitPeriodsEnum.daily,
        type,
        mediatorType,
      });
    }

    if (weekly && defaultValues.weeklyLimit !== weekly) {
      onSubmit({
        amount: weekly,
        period: LimitPeriodsEnum.weekly,
        type,
        mediatorType,
      });
    }

    if (monthly && defaultValues.monthlyLimit !== monthly) {
      onSubmit({
        amount: monthly,
        period: LimitPeriodsEnum.monthly,
        type,
        mediatorType,
      });
    }
  };

  const limitMessagesMap: Record<string, { p1: string; p2: string; existing: string; set: string }> = {
    monetary: {
      p1: 'deposit-limits.decrease-message-p1',
      p2: 'deposit-limits.decrease-message-p2',
      existing: 'deposit-limits.existing',
      set: 'deposit-limits.set',
    },
    sports: {
      p1: 'sport-loss-limits.decrease-message-p1',
      p2: 'sport-loss-limits.decrease-message-p2',
      existing: 'sport-loss-limits.existing',
      set: 'sport-loss-limits.set',
    },
    casino: {
      p1: 'casino-loss-limits.decrease-message-p1',
      p2: 'casino-loss-limits.decrease-message-p2',
      existing: 'casino-loss-limits.existing',
      set: 'casino-loss-limits.set',
    },
  };

  const limitMessageSelected = limitMessagesMap[mediatorType];

  return (
    <PopUpHandler
      header={t('limits.header.title', { title })}
      drawerProps={{
        backdropCanClose: true,
        className: 'account-limits-modal',
      }}
      modalProps={{ 'aria-label': 'Account Limits' }}
      backdropClassName="account-limits-modal"
      headingProps={{ headingLevel: 5 }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Paragraph className="account-limits-modal__description">{t(limitMessageSelected.p1)}</Paragraph>
      <Paragraph className="account-limits-modal__description">{t(limitMessageSelected.p2)}</Paragraph>
      <Paragraph className="account-limits-modal__description">{t('limits.decrease-message-p3')}</Paragraph>
      <Paragraph
        className="account-limits-modal__description"
        dangerouslySetInnerHTML={{ __html: t('customer-server-limits-message-p4') }}
      />
      {defaultValues.dailyLimit || defaultValues.weeklyLimit || defaultValues.monthlyLimit ? (
        <Paragraph className="account-limits-modal__existing">{t(limitMessageSelected.existing)}</Paragraph>
      ) : (
        <Paragraph className="account-limits-modal__existing">{t(limitMessageSelected.set)}</Paragraph>
      )}

      {defaultValues.dailyLimit && (
        <Paragraph className="account-limits-modal__limits">
          <span>Daily:</span>
          <span>£{defaultValues.dailyLimit}</span>
        </Paragraph>
      )}

      {defaultValues.weeklyLimit && (
        <Paragraph className="account-limits-modal__limits">
          <span>Weekly:</span>
          <span>£{defaultValues.weeklyLimit}</span>
        </Paragraph>
      )}

      {defaultValues.monthlyLimit && (
        <Paragraph className="account-limits-modal__limits">
          <span>Monthly:</span>
          <span>£{defaultValues.monthlyLimit}</span>
        </Paragraph>
      )}

      <Form
        id="Deposit Limits"
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        FormActions={({ submitDisabled }): JSX.Element => (
          <>
            <div className="deposit-limits-modal__btn-container">
              <Button type="button" variant="secondary-alt" onClick={onClose}>
                {t('buttons.cancel.uppercase')}
              </Button>
              <Button type="submit" disabled={submitDisabled}>
                {t('buttons.submit.uppercase')}
              </Button>
            </div>
          </>
        )}
      >
        <div className="account-limits-modal__form-row">
          <FormControl
            type={FormFieldType.CurrencyInput}
            name="dailyLimit"
            label={
              getPendingItemByPeriod(LimitPeriodsEnum.daily)
                ? `${t('limits.dayly-label', { title })} - PENDING`
                : t('limits.dayly-label', { title })
            }
            rules={validation.dailyDeposit}
            disabled={Boolean(getPendingItemByPeriod(LimitPeriodsEnum.daily))}
            props={{
              theme: 'dark',
              placeholder: '£',
            }}
          />
        </div>

        <div className="account-limits-modal__form-row">
          <FormControl
            type={FormFieldType.CurrencyInput}
            name="weeklyLimit"
            label={
              getPendingItemByPeriod(LimitPeriodsEnum.weekly)
                ? `${t('limits.weekly-label', { title })} - PENDING`
                : t('limits.weekly-label', { title })
            }
            rules={validation.weeklyDeposit}
            disabled={Boolean(getPendingItemByPeriod(LimitPeriodsEnum.weekly))}
            props={{
              theme: 'dark',
              placeholder: '£',
            }}
          />
        </div>

        <div className="account-limits-modal__form-row">
          <FormControl
            type={FormFieldType.CurrencyInput}
            name="monthlyLimit"
            label={
              getPendingItemByPeriod(LimitPeriodsEnum.monthly)
                ? `${t('limits.monthly-label', { title })} - PENDING`
                : t('limits.monthly-label', { title })
            }
            rules={validation.monthlyDeposit}
            disabled={Boolean(getPendingItemByPeriod(LimitPeriodsEnum.monthly))}
            props={{
              theme: 'dark',
              placeholder: '£',
            }}
          />
        </div>
      </Form>
    </PopUpHandler>
  );
};
